import { PractitionerModel } from '@ctw/shared/api/fhir/models/practitioner';
import { formatFHIRDateOrDatetime } from '@ctw/shared/utils/dates';
import type { Basic, DomainResource, EpisodeOfCare, Extension, Provenance } from 'fhir/r4';
import { capitalize, compact, uniq } from 'lodash-es';
import { codeableConceptLabel, findAllCodings, hasMatchingCode } from '../codeable-concept';
import { findReference } from '../resource-helper';
import { SYSTEM_DIAGNOSIS_ROLE, SYSTEM_SNOMED, SYSTEM_ZUS_CREATED_AT } from '../system-urls';
import type { ResourceMap } from '../types';
import type { EncounterModel } from './encounter';
import { FHIRModel } from './fhir-model';
import { PatientModel } from './patient';

const DISCHARGE_NARRATIVE_URL = 'https://zusapi.com/lens/extension/dischargeNarrative';
const PREFERRED_DISCHARGE_NARRATIVE_URL =
  'https://zusapi.com/lens/extension/preferredDischargeNarrative';
const REASONS_URL = 'https://zusapi.com/lens/extension/reasons';
const CURRENT_PARTICIPANTS_URL = 'https://zusapi.com/lens/extension/currentParticipants';
const NONELECTIVE_ADMISSION_URL = 'https://zusapi.com/lens/extension/nonelectiveAdmission';

// Location extensions
const LOC_URL = 'https://zusapi.com/lens/extension/location';
const START_DATE_URL = 'https://zusapi.com/lens/extension/highestPrecisionStart';
const END_DATE_URL = 'https://zusapi.com/lens/extension/highestPrecisionEnd';
const DISCHARGE_DISP_URL = 'https://zusapi.com/lens/extension/dischargeDisposition';
const DISCHARGE_LOC_URL = 'https://zusapi.com/lens/extension/dischargeLocation';
const ADMIT_LOC_URL = 'https://zusapi.com/lens/extension/admittingLocation';
const EMERGENCY_LOC_URL = 'https://zusapi.com/lens/extension/emergencyLocationDetails';
const CURRENT_LOC_DETAILS_URL = 'https://zusapi.com/lens/extension/currentLocationDetails';

const findViaExtensionByURL = (r: DomainResource | Extension | undefined, url: string) =>
  r?.extension?.find((e) => e.url === url);

export class EpisodeOfCareModel extends FHIRModel<EpisodeOfCare> {
  public kind = 'EpisodeOfCareModel' as const;

  public binaryId?: string;

  public provenance: Array<Provenance>;

  public relatedEncounters: Array<EncounterModel> | undefined;

  public constructor(
    resource: EpisodeOfCare,
    provenance: Array<Provenance>,
    includedResources?: ResourceMap,
    basics?: Array<Basic>,
  ) {
    super(resource, includedResources, basics);
    this.provenance = provenance;
  }

  public get key() {
    return this.resource.id || '';
  }

  public get lastUpdated(): string | undefined {
    return (
      this.resource.meta?.lastUpdated ||
      this.resource.meta?.extension?.find((e) => e.url === SYSTEM_ZUS_CREATED_AT)?.valueDateTime
    );
  }

  public get patient(): PatientModel | undefined {
    const patient = findReference('Patient', undefined, undefined, this.resource.patient);
    return patient ? new PatientModel(patient) : undefined;
  }

  public get diagnoses(): Array<string> | undefined {
    return compact(
      uniq(
        this.resource.diagnosis?.map((d) => {
          const c = findReference('Condition', undefined, undefined, d.condition);
          return c?.code?.text;
        }),
      ),
    );
  }

  public diagnosesForRole(code: string): Array<string> {
    const diagnosis = this.resource.diagnosis?.filter((d) =>
      hasMatchingCode(SYSTEM_DIAGNOSIS_ROLE, [code], d.role),
    );

    return compact(
      uniq(
        diagnosis?.map((d) => {
          const c = findReference('Condition', undefined, undefined, d.condition);
          const codes = findAllCodings(SYSTEM_SNOMED, c?.code);
          return codes[0]?.display;
        }),
      ),
    );
  }

  public get class(): string | undefined {
    const classExt = findViaExtensionByURL(
      this.resource,
      'https://zusapi.com/lens/extension/encounterClass',
    );
    return capitalize(classExt?.valueCoding?.display);
  }

  public get classCode(): string {
    const classExt = findViaExtensionByURL(
      this.resource,
      'https://zusapi.com/lens/extension/encounterClass',
    );
    return classExt?.valueCoding?.code?.toUpperCase() ?? '';
  }

  public get dischargeDisposition(): string | undefined {
    const dischargeExt = findViaExtensionByURL(this.resource, DISCHARGE_DISP_URL);
    return codeableConceptLabel(dischargeExt?.valueCodeableConcept);
  }

  public get location(): string | undefined {
    const dischargeLoc = findViaExtensionByURL(this.resource, DISCHARGE_LOC_URL);
    const currentLoc = findViaExtensionByURL(
      findViaExtensionByURL(this.resource, CURRENT_LOC_DETAILS_URL),
      LOC_URL,
    );
    return (
      dischargeLoc?.valueReference?.display ||
      currentLoc?.valueReference?.display || // We can remove this line if all encounter lenses are v2.
      this.admittingLocation
    );
  }

  public get admittingLocation(): string | undefined {
    const admitLoc = findViaExtensionByURL(this.resource, ADMIT_LOC_URL);
    return admitLoc?.valueReference?.display;
  }

  public get periodEnd() {
    const end =
      findViaExtensionByURL(this.resource, END_DATE_URL)?.valueDateTime ??
      this.resource.period?.end;
    return end ? formatFHIRDateOrDatetime(end) : undefined;
  }

  public get periodStart() {
    const start =
      findViaExtensionByURL(this.resource, START_DATE_URL)?.valueDateTime ??
      this.resource.period?.start;
    return start ? formatFHIRDateOrDatetime(start) : undefined;
  }

  public get dateDisplay() {
    if (this.periodStart !== this.periodEnd && this.periodEnd) {
      return `${this.periodStart} - ${this.periodEnd}`;
    }
    return this.periodStart;
  }

  public get status() {
    const emergencyLoc = findViaExtensionByURL(this.resource, EMERGENCY_LOC_URL);
    if (emergencyLoc?.valueReference) {
      return 'In ED';
    }
    if (this.periodEnd) {
      return 'Discharged';
    }
    if (this.classCode === 'IMP') {
      return 'Admitted to Inpatient';
    }
    return this.resource.status;
  }

  public get dischargeBinaryIds(): Array<string> {
    const dischargeExt = findViaExtensionByURL(this.resource, DISCHARGE_NARRATIVE_URL);
    return compact(
      dischargeExt?.extension?.map((e) => e.valueReference?.reference?.substring('Binary/'.length)),
    );
  }

  public get preferredDischargeBinaryIds(): Array<string> {
    const dischargeExtensions =
      findViaExtensionByURL(this.resource, DISCHARGE_NARRATIVE_URL)?.extension ?? [];
    const preferred = dischargeExtensions.filter((extension) =>
      extension.valueReference?.extension?.some(
        (e) => e.url === PREFERRED_DISCHARGE_NARRATIVE_URL && e.valueBoolean === true,
      ),
    );
    return compact(preferred.map((e) => e.valueReference?.reference?.substring('Binary/'.length)));
  }

  public get title() {
    return `${this.dateDisplay} - ${this.class}`;
  }

  public get admissionType(): string | undefined {
    return this.resource.extension?.find((e) => e.url === NONELECTIVE_ADMISSION_URL)?.valueString;
  }

  public get reasonDisplays(): Array<string> {
    return (
      uniq(
        this.resource.extension
          ?.find((e) => e.url === REASONS_URL)
          ?.extension?.map((e) => {
            return codeableConceptLabel(e.valueCodeableConcept);
          }),
      ) ?? []
    );
  }

  public get currentParticipantsNames(): Array<string> {
    return (
      uniq(
        this.resource.extension
          ?.find((e) => e.url === CURRENT_PARTICIPANTS_URL)
          ?.extension?.map((e) => {
            const referenceDisplay = e.valueReference?.display;
            if (!!referenceDisplay && referenceDisplay !== '') {
              return referenceDisplay;
            }

            const practitioner = findReference(
              'Practitioner',
              undefined,
              undefined,
              e.valueReference,
            );
            return practitioner
              ? new PractitionerModel(practitioner).fullName
              : `ID: ${e.valueReference?.reference?.split('/')[1]}`;
          }),
      ) ?? []
    );
  }
}
